/**
 * Booking-slot editor — manages parallel resources on the user's
 * calendar. N slots = N parallel appointments at the same clock time.
 *
 * Slot identity is the immutable `id` (slug of the first-saved name).
 * Renaming preserves the id so existing calendar events stay attached
 * to their column in the day-view.
 */
import * as React from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Users } from 'lucide-react';
import type { BookingSlot, BookingSlotColorTone } from '@cp3/shared';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Button } from '../../../../components/ui/button';
import { useEventTypes } from '../../../../hooks/useEventTypes';
import { cn } from '../../../../lib/utils';
import { usePhoneSettingsForm } from '../PhoneSettingsContext';

const TONES: ReadonlyArray<{ id: BookingSlotColorTone; label: string; chip: string }> = [
  { id: 'slate', label: 'Schiefer', chip: 'bg-slate-500/40 ring-slate-500/40' },
  { id: 'sky', label: 'Himmelblau', chip: 'bg-sky-500/40 ring-sky-500/40' },
  { id: 'emerald', label: 'Smaragd', chip: 'bg-emerald-500/40 ring-emerald-500/40' },
  { id: 'amber', label: 'Bernstein', chip: 'bg-amber-500/40 ring-amber-500/40' },
  { id: 'rose', label: 'Rosé', chip: 'bg-rose-500/40 ring-rose-500/40' },
  { id: 'violet', label: 'Violett', chip: 'bg-violet-500/40 ring-violet-500/40' },
];

function slugify(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 32);
}

function nextUniqueId(name: string, existing: BookingSlot[]): string {
  const base = slugify(name) || 'slot';
  let candidate = base;
  let n = 2;
  const ids = new Set(existing.map((s) => s.id));
  while (ids.has(candidate)) {
    candidate = `${base}-${n}`;
    n++;
  }
  return candidate;
}

export function BookingSlotsCard() {
  const { form, setForm } = usePhoneSettingsForm();
  const eventTypes = useEventTypes();
  const slots: BookingSlot[] = form.bookingSlots ?? [];
  const eventTypeList = eventTypes.data ?? [];

  function update(next: BookingSlot[]) {
    setForm((p) => ({ ...p, bookingSlots: next }));
  }

  function addSlot() {
    const name = `Slot ${slots.length + 1}`;
    const id = nextUniqueId(name, slots);
    const colorTone: BookingSlotColorTone = TONES[slots.length % TONES.length]!.id;
    update([...slots, { id, name, colorTone, eventTypeIds: [] }]);
  }

  function patchSlot(idx: number, patch: Partial<BookingSlot>) {
    update(slots.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }

  function removeSlot(idx: number) {
    update(slots.filter((_, i) => i !== idx));
  }

  function moveSlot(idx: number, direction: -1 | 1) {
    const target = idx + direction;
    if (target < 0 || target >= slots.length) return;
    const next = slots.slice();
    const tmp = next[idx]!;
    next[idx] = next[target]!;
    next[target] = tmp;
    update(next);
  }

  function toggleEventType(idx: number, eventTypeId: string) {
    const slot = slots[idx]!;
    const list = slot.eventTypeIds ?? [];
    const has = list.includes(eventTypeId);
    const nextList = has ? list.filter((x) => x !== eventTypeId) : [...list, eventTypeId];
    patchSlot(idx, { eventTypeIds: nextList });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          Parallele Slots / Buchungskapazität
        </CardTitle>
        <CardDescription>
          Lege benannte parallele Slots an — z. B. eine Spalte pro Mitarbeiterin
          („Barbara", „Anna") oder pro Service-Linie („Mundhygiene",
          „Prophylaxe"). Anzahl Slots = wie viele Termine gleichzeitig
          gebucht werden können. Die KI ordnet eine Buchung dem genannten
          Slot zu (Name oder Termin-Typ-Filter); ohne Präferenz wird
          load-balanced.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {slots.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            Noch keine Slots — der Kalender verhält sich wie ein einzelner
            „Standard"-Slot. Füge einen zweiten hinzu, um Parallel-Buchungen zu
            erlauben.
          </div>
        ) : (
          <ul className="space-y-3">
            {slots.map((slot, idx) => {
              const tone = TONES.find((t) => t.id === slot.colorTone) ?? TONES[0]!;
              const filterCount = slot.eventTypeIds?.length ?? 0;
              return (
                <li
                  key={slot.id}
                  className="space-y-3 rounded-md border border-border p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={cn(
                        'inline-block size-3 shrink-0 rounded-full ring-2 ring-offset-2 ring-offset-background',
                        tone.chip,
                      )}
                      aria-hidden
                    />
                    <Input
                      value={slot.name}
                      onChange={(e) => patchSlot(idx, { name: e.target.value })}
                      placeholder="Slot-Name (z. B. Barbara)"
                      className="h-8 max-w-[260px] flex-1"
                    />
                    <div className="ml-auto flex items-center gap-1">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => moveSlot(idx, -1)}
                        disabled={idx === 0}
                        className="h-8 w-8"
                        aria-label="Hoch"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => moveSlot(idx, 1)}
                        disabled={idx === slots.length - 1}
                        className="h-8 w-8"
                        aria-label="Runter"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeSlot(idx)}
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        aria-label="Slot löschen"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-[1fr_1fr]">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Farbe (Tagesansicht)</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {TONES.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => patchSlot(idx, { colorTone: t.id })}
                            className={cn(
                              'h-6 w-6 rounded-full ring-2 transition-transform',
                              t.chip,
                              slot.colorTone === t.id
                                ? 'scale-110 ring-foreground/60'
                                : 'ring-transparent hover:scale-105',
                            )}
                            aria-label={t.label}
                            title={t.label}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">
                        Termin-Typ-Filter
                        {filterCount > 0 && (
                          <span className="ml-1 text-muted-foreground">
                            ({filterCount} aktiv)
                          </span>
                        )}
                      </Label>
                      <div className="flex flex-wrap gap-1.5">
                        {eventTypeList.length === 0 ? (
                          <span className="text-xs text-muted-foreground">
                            Lege zuerst Termin-Typen an.
                          </span>
                        ) : (
                          eventTypeList.map((et) => {
                            const active = slot.eventTypeIds?.includes(et.id) ?? false;
                            return (
                              <button
                                key={et.id}
                                type="button"
                                onClick={() => toggleEventType(idx, et.id)}
                                className={cn(
                                  'rounded-full border px-2 py-0.5 text-[11px] transition-colors',
                                  active
                                    ? 'border-primary bg-primary/15 text-primary'
                                    : 'border-border text-muted-foreground hover:bg-secondary',
                                )}
                              >
                                {et.name}
                              </button>
                            );
                          })
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        Leer = jeder Termin-Typ. Sonst nur ausgewählte.
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {slots.length === 0
              ? 'Kapazität: 1 paralleler Termin (impliziter Standard-Slot).'
              : `Kapazität: ${slots.length} parallele${slots.length === 1 ? 'r' : ''} Termin${
                  slots.length === 1 ? '' : 'e'
                } pro Zeitpunkt.`}
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={addSlot}
            disabled={slots.length >= 20}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Slot hinzufügen
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
